import * as Sentry from '@sentry/react';
import { useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';

import { ToastControlsMenu } from '@apps/web/components/notifications/toast_controls_menu';
import { useSessionState } from '@apps/web/context/session_state_provider';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    type ConnectCredential,
    useWebSocket,
} from '@apps/web/hooks/use_websocket';
import {
    getActiveAgentConversation,
    openAgentConversation,
} from '@apps/web/lib/active_agent_conversation';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { persistAgentChatOpenIntent } from '@apps/web/lib/chat_ui_persistence';
import {
    createClickableToastClassName,
    registerClickableToast,
    unregisterClickableToast,
} from '@apps/web/lib/clickable_toasts';
import {
    isOrganizationEventTestEnvironment,
    ORGANIZATION_EVENTS_CONNECTION_CLOSED_EVENT,
    ORGANIZATION_EVENTS_CONNECTION_OPENED_EVENT,
    ORGANIZATION_EVENTS_GAP_DETECTED_EVENT,
    ORGANIZATION_EVENTS_RECONNECTED_EVENT,
    organizationEventBus,
    type OrganizationEventBusDetail,
} from '@apps/web/lib/event_bus';
import { EVENTS } from '@apps/web/lib/events';
import {
    isEmbeddedExtensionFrame,
    isExtensionContext,
} from '@apps/web/lib/extension_context';
import { getExtensionSessionToken } from '@apps/web/lib/extension_session_token';
import { modeSchema, useMode } from '@apps/web/lib/mode_store';
import {
    buildNotificationNavigationUrl,
    getNotificationNavigationRouteOptions,
    getNotificationNavigationTarget,
} from '@apps/web/lib/notification_navigation';
import { isNotificationChannelEnabled } from '@apps/web/lib/notification_preferences';
import { isNotificationRecipient } from '@apps/web/lib/notification_recipients';
import {
    buildNotificationToastId,
    markNotificationsRead,
} from '@apps/web/lib/notification_state_actions';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    createOrganizationWebsocketTicket,
    useNotificationPreferences,
    useOrganization,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type Mode } from '@libraries/typescript/api_client';

// See `libraries/python/organization_events` for the backend schemas.

const resourceTypeSchema = z.enum([
    'organization',
    'conversation',
    'message',
    'outreach',
    'caregiver',
    'staged_message',
    'shift',
    'shift_confirmation',
    'contact_attempt',
    'callout',
    'agent_conversation',
    'agent_turn',
    'agent_run',
    'voice_call',
    'activity_event',
    'user_settings',
]);

const resourceRefSchema = z.object({
    type: resourceTypeSchema,
    id: z.uuid(),
});

const baseOrganizationEventSchema = z.object({
    timestamp: z.iso.datetime({ offset: true }),
    resource: resourceRefSchema,
    relationships: z.array(resourceRefSchema),
    // Per-org publish sequence; null/absent on user-channel events.
    org_seq: z.number().int().nullable().optional(),
    // Mode of the write that produced the event; absent from older servers.
    mode: modeSchema.nullable().optional(),
});

// The one definition of "this frame carries a sequence" — applied to the raw
// frame so unknown event types and sequence-only suppression stubs count.
const organizationEventSequenceSchema = z.object({
    org_seq: z.number().int().positive(),
});

const notificationDataSchema = z.object({
    title: z.string().nullable(),
    message: z.string().nullable(),
    in_app_title: z.string().nullable().optional(),
    in_app_message: z.string().nullable().optional(),
    notification_id: z.uuid().nullable().optional(),
    data: z.record(z.string(), z.unknown()).nullable(),
});

const notificationStateUpdateSchema = z.object({
    notification_id: z.uuid(),
    is_read: z.boolean(),
    is_dismissed: z.boolean(),
});

const notificationStateUpdateDataSchema = z.object({
    updates: z.array(notificationStateUpdateSchema),
});

const agentConversationStreamDataSchema = z.object({
    type: z.enum([
        'run_started',
        'reasoning_chunk',
        'response_chunk',
        'response_classified',
        'tool_call_start',
        'tool_call_complete',
        'activity_update',
        'run_complete',
        'run_error',
        'subagent_drafts_started',
        'subagent_drafts_unit_trace',
        'subagent_drafts_unit_complete',
    ]),
    run_id: z.uuid(),
    node_id: z.string(),
    sequence: z.number().int().nonnegative(),
    parent_node_id: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    phase: z.enum(['thinking', 'tool', 'responding']).nullable().optional(),
    label: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
    tool_call_id: z.string().nullable().optional(),
    tool_name: z.string().nullable().optional(),
    arguments: z.unknown().nullable().optional(),
    result: z.unknown().nullable().optional(),
    error: z.string().nullable().optional(),
    duration_ms: z.number().int().nonnegative().nullable().optional(),
    response_kind: z.enum(['interstitial', 'final']).nullable().optional(),
});

const organizationEventWithoutPayloadSchema =
    baseOrganizationEventSchema.extend({
        type: z.enum([
            'message_created',
            'conversation_updated',
            'pending_message_updated',
            'contact_attempt_updated',
            'shifts_updated',
            'shift_confirmation_updated',
            'conversations_unread_updated',
            'conversations_mark_all_read',
            'agent_conversation_updated',
            'agent_turn_updated',
            'voice_call_updated',
            'outreach_suggestion_added',
            'outreach_suggestion_removed',
            'outreach_updated',
            'agent_automations_updated',
            'activity_event_created',
        ]),
        data: z.null(),
    });

const organizationNotificationEventSchema = baseOrganizationEventSchema.extend({
    type: z.enum([
        'notification_info',
        'notification_success',
        'notification_warning',
        'notification_error',
    ]),
    data: notificationDataSchema,
});

const organizationAgentConversationStreamEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('agent_conversation_stream_event'),
        data: agentConversationStreamDataSchema,
    });

const organizationAgentRunStreamEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('agent_run_stream_event'),
        data: agentConversationStreamDataSchema,
    });

const organizationNotificationStateUpdatedEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('notification_state_updated'),
        data: notificationStateUpdateDataSchema,
    });

const syncProgressDataSchema = z.object({
    sync_log_id: z.uuid(),
    entity_type: z.enum([
        'shifts',
        'caregivers',
        'clients',
        'care_coordinators',
    ]),
    phase: z.enum(['saving', 'finalizing']),
    items_processed: z.number().int().nonnegative().nullable().optional(),
    items_total: z.number().int().nonnegative().nullable().optional(),
    last_updated_at: z.iso.datetime({ offset: true }),
});

const organizationSyncProgressUpdatedEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('sync_progress_updated'),
        data: syncProgressDataSchema,
    });

const chatTypingDataSchema = z.object({
    run_id: z.uuid(),
    user_id: z.uuid(),
    display_name: z.string(),
    typing: z.boolean(),
});

const organizationChatTypingEventSchema = baseOrganizationEventSchema.extend({
    type: z.literal('chat_typing'),
    data: chatTypingDataSchema,
});

const conversationTypingDataSchema = z.object({
    conversation_id: z.uuid(),
    user_id: z.uuid(),
    display_name: z.string(),
    typing: z.boolean(),
});

const organizationConversationTypingEventSchema =
    baseOrganizationEventSchema.extend({
        type: z.literal('conversation_typing'),
        data: conversationTypingDataSchema,
    });

const incomingCallDataSchema = z.object({
    call_id: z.uuid(),
    organization_id: z.uuid(),
    from_number_display: z.string(),
    caller_label: z.string().nullable(),
    line_label: z.string().nullable(),
    ring_started_at: z.iso.datetime({ offset: true }),
});

const organizationIncomingCallEventSchema = baseOrganizationEventSchema.extend({
    type: z.literal('incoming_call'),
    data: incomingCallDataSchema,
});

// The "Approval needed" side pop must mirror the chat surface: pop only when
// the run still has a pending approval, i.e. an approval card the user can
// act on. A call auto-approved by an always-allow grant, or decided before
// the alert lands, renders no card — so no pop. Fails open on fetch errors:
// the alert exists to reach a user who navigated away.
async function agentRunStillAwaitsApproval(
    organizationId: string,
    agentRunId: string,
    mode: Mode,
): Promise<boolean> {
    try {
        const runState = await getApiClient().get({
            url: '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}',
            pathParams: {
                organization_id: organizationId,
                phoebe_agent_run_id: agentRunId,
            },
            // A gate check is not the user viewing the run: it must not
            // clear their unread approval bell alerts.
            queryParams: { mark_viewed: false },
            mode,
        });
        return runState.has_pending_approval;
    } catch {
        return true;
    }
}

// The user is already looking at the agent chat this notification points
// at — surfacing it would just nag them. Run-native chats select by run id,
// so match either id.
function isViewingAgentConversation(
    target: ReturnType<typeof getNotificationNavigationTarget>,
    organizationId: string | null,
): boolean {
    const activeAgentConversation = getActiveAgentConversation(organizationId);
    return (
        target?.kind === 'agent_conversation' &&
        (activeAgentConversation === target.agentConversationId ||
            (target.agentRunId !== null &&
                activeAgentConversation === target.agentRunId))
    );
}

export const organizationEventSchema = z.discriminatedUnion('type', [
    organizationEventWithoutPayloadSchema,
    organizationNotificationEventSchema,
    organizationNotificationStateUpdatedEventSchema,
    organizationAgentConversationStreamEventSchema,
    organizationAgentRunStreamEventSchema,
    organizationSyncProgressUpdatedEventSchema,
    organizationChatTypingEventSchema,
    organizationConversationTypingEventSchema,
    organizationIncomingCallEventSchema,
]);

export type OrganizationEventType = OrganizationEvent['type'];

export type OrganizationEvent = z.infer<typeof organizationEventSchema>;

// Completeness-checked registry of every event type this build understands.
// Typed as Record<OrganizationEventType, true> so adding a union member without
// listing it here is a compile error.
const KNOWN_ORGANIZATION_EVENT_TYPES: Record<OrganizationEventType, true> = {
    message_created: true,
    conversation_updated: true,
    pending_message_updated: true,
    contact_attempt_updated: true,
    shifts_updated: true,
    shift_confirmation_updated: true,
    conversations_unread_updated: true,
    conversations_mark_all_read: true,
    agent_conversation_updated: true,
    agent_turn_updated: true,
    voice_call_updated: true,
    outreach_suggestion_added: true,
    outreach_suggestion_removed: true,
    outreach_updated: true,
    agent_automations_updated: true,
    activity_event_created: true,
    notification_info: true,
    notification_success: true,
    notification_warning: true,
    notification_error: true,
    notification_state_updated: true,
    agent_conversation_stream_event: true,
    agent_run_stream_event: true,
    sync_progress_updated: true,
    chat_typing: true,
    conversation_typing: true,
    incoming_call: true,
};

function isKnownOrganizationEventType(rawData: unknown): boolean {
    if (typeof rawData !== 'object' || rawData === null) {
        return false;
    }
    const type = (rawData as { readonly type?: unknown }).type;
    return (
        typeof type === 'string' &&
        Object.prototype.hasOwnProperty.call(
            KNOWN_ORGANIZATION_EVENT_TYPES,
            type,
        )
    );
}

type NotificationClickBehavior = 'navigate' | 'new-tab';
type NotificationTelemetrySurface = 'web_app' | 'extension';
type RealtimeNotificationEvent = Extract<
    OrganizationEvent,
    {
        type:
            | 'notification_info'
            | 'notification_success'
            | 'notification_warning'
            | 'notification_error';
    }
>;

const TOAST_BURST_WINDOW_MS = 15_000;
const MAX_TOASTS_PER_BURST = 3;

type ConversationToastSuppression = 'duplicate' | 'throttled';

// A malformed or schema-drifted websocket event can arrive at the same high
// frequency as legitimate ones (e.g. streamed agent chunks during an active
// run), so logging and Sentry-reporting every failure unbounded turns one bad
// message shape into a runaway console-error loop — PHO-13440 observed up to
// ~5.4M console errors in a single /conversations session. Cap how often the
// parse failure is logged and reported per rolling window so a sustained
// failure stays observable without flooding the console or Sentry. State is
// module-level so the cap bounds total volume across every mounted listener.
const WS_EVENT_PARSE_ERROR_LOG_WINDOW_MS = 60_000;
const MAX_WS_EVENT_PARSE_ERROR_LOGS_PER_WINDOW = 5;

let wsEventParseErrorWindowStartMs = 0;
let wsEventParseErrorLogCount = 0;
let wsEventParseErrorSuppressedCount = 0;

function reportWebSocketEventParseError(error: unknown): void {
    const nowMs = Date.now();
    if (
        nowMs - wsEventParseErrorWindowStartMs >=
        WS_EVENT_PARSE_ERROR_LOG_WINDOW_MS
    ) {
        if (wsEventParseErrorSuppressedCount > 0) {
            console.error(
                `Suppressed ${wsEventParseErrorSuppressedCount} further WebSocket event parse errors in the last ${
                    WS_EVENT_PARSE_ERROR_LOG_WINDOW_MS / 1_000
                }s`,
            );
        }
        wsEventParseErrorWindowStartMs = nowMs;
        wsEventParseErrorLogCount = 0;
        wsEventParseErrorSuppressedCount = 0;
    }

    if (wsEventParseErrorLogCount >= MAX_WS_EVENT_PARSE_ERROR_LOGS_PER_WINDOW) {
        wsEventParseErrorSuppressedCount += 1;
        return;
    }

    wsEventParseErrorLogCount += 1;
    Sentry.captureException(error);
    console.error('Failed to parse WebSocket event', error);
}

function isRealtimeNotificationEvent(
    eventData: OrganizationEvent,
): eventData is RealtimeNotificationEvent {
    return (
        eventData.type === 'notification_info' ||
        eventData.type === 'notification_success' ||
        eventData.type === 'notification_error' ||
        eventData.type === 'notification_warning'
    );
}

function getNotificationPayloadData(
    eventData: RealtimeNotificationEvent,
): Record<string, unknown> | null | undefined {
    return eventData.data.data as Record<string, unknown> | null | undefined;
}

const GENERIC_EXTERNAL_NOTIFICATION_TITLE = 'Phoebe notification';

function getInAppNotificationText(
    eventData: RealtimeNotificationEvent,
    key: 'in_app_title' | 'in_app_message',
    fallback: string | null,
): string | null {
    return eventData.data[key] ?? fallback;
}

function getExternalNotificationContent(eventData: RealtimeNotificationEvent): {
    readonly title: string;
    readonly message: string | null;
} {
    return {
        title: eventData.data.title ?? GENERIC_EXTERNAL_NOTIFICATION_TITLE,
        message: eventData.data.message,
    };
}

function getNotificationStatus(
    eventData: RealtimeNotificationEvent,
): string | null {
    const data = getNotificationPayloadData(eventData);
    return typeof data?.['status'] === 'string' ? data['status'] : null;
}

function getNotificationMode(
    eventData: RealtimeNotificationEvent,
): string | null {
    const data = getNotificationPayloadData(eventData);
    return typeof data?.['mode'] === 'string' ? data['mode'] : null;
}

function buildFallbackNotificationId(
    eventData: RealtimeNotificationEvent,
): string {
    return [
        eventData.type,
        eventData.resource.type,
        eventData.resource.id,
        eventData.timestamp,
        eventData.data.title ?? '',
        eventData.data.message ?? '',
        getNotificationStatus(eventData) ?? '',
    ].join(':');
}

function getNotificationId(eventData: RealtimeNotificationEvent): string {
    return (
        eventData.data.notification_id ?? buildFallbackNotificationId(eventData)
    );
}

// The burst rate limit exists to bound high-volume, low-severity toast
// spam (callout acceptances, clock in/out) — it applies to info/success
// toasts on every page, not just /conversations. Error and warning toasts
// are low-volume by nature and are often the only signal something went
// wrong, so they stay exempt from the shared budget entirely.
function shouldRateLimitToast(eventData: RealtimeNotificationEvent): boolean {
    return (
        eventData.type === 'notification_info' ||
        eventData.type === 'notification_success'
    );
}

// The duplicate-toast check is conversation-scoped: it relies on a
// deterministic dedupe key that only makes sense for toasts surfaced while
// viewing /conversations, so it stays narrower than the rate limit above.
function shouldBoundConversationToast(
    eventData: RealtimeNotificationEvent,
    telemetrySurface: NotificationTelemetrySurface,
): boolean {
    if (
        telemetrySurface !== 'web_app' ||
        eventData.type === 'notification_error' ||
        eventData.type === 'notification_warning' ||
        typeof window === 'undefined'
    ) {
        return false;
    }

    return (
        window.location.pathname === '/conversations' ||
        window.location.pathname === '/conversations/'
    );
}

function buildConversationToastKey(
    eventData: RealtimeNotificationEvent,
    navigationTarget: ReturnType<typeof getNotificationNavigationTarget>,
): string {
    return JSON.stringify([
        eventData.type,
        eventData.data.title,
        eventData.data.message,
        getNotificationStatus(eventData),
        navigationTarget ?? {
            kind: 'resource',
            resourceId: eventData.resource.id,
            resourceType: eventData.resource.type,
        },
    ]);
}

function shouldShowNativeBrowserNotification(): boolean {
    if (typeof document === 'undefined') {
        return false;
    }

    return document.visibilityState !== 'visible' || !document.hasFocus();
}

function shouldShowBrowserNotificationForEvent(
    eventData: RealtimeNotificationEvent,
): boolean {
    return !(
        eventData.resource.type === 'shift' &&
        getNotificationStatus(eventData) === 'confirmed'
    );
}

export function useOrganizationEvents(
    organizationId: string | null,
    userId: string | null,
    options?: {
        readonly browserNotificationsEnabled?: boolean;
        readonly notificationChannelEnabled?: boolean;
        readonly notificationClickBehavior?: NotificationClickBehavior;
        readonly skipInAppNotificationPreferenceChecks?: boolean;
        readonly telemetrySurface?: NotificationTelemetrySurface;
        readonly mode?: Mode;
    },
) {
    const navigate = useNavigate();
    const posthog = usePostHog();
    const { revalidateSession } = useSessionState();
    const mode = useMode();
    const effectiveMode = options?.mode ?? mode ?? 'live';
    const { data: notificationPreferences } = useNotificationPreferences(
        organizationId,
        'organization-events',
        effectiveMode,
    );
    const { data: userProfile } = useUserProfile({
        reason: 'organization-events',
    });
    const { data: notificationOrganization, isLoading: isOrganizationLoading } =
        useOrganization(organizationId, {
            reason: 'organization-events-timezone',
        });
    const {
        isLoading: isContextOrganizationTimezoneLoading,
        timezone: contextOrganizationTimezone,
    } = useOrganizationTimezone();
    const organizationTimezone =
        notificationOrganization?.timezone ??
        (organizationId === null ? contextOrganizationTimezone : null);
    const isOrganizationTimezoneLoading =
        organizationId === null
            ? isContextOrganizationTimezoneLoading
            : isOrganizationLoading;
    const userTimezone = userProfile?.user.timezone ?? null;
    const userTimezoneIsMissing =
        userProfile?.user.timezone === null ||
        userProfile?.user.timezone === undefined;
    const shouldSkipNotificationSchedule =
        userProfile === undefined ||
        (userTimezoneIsMissing &&
            (isOrganizationTimezoneLoading || organizationTimezone === null));
    const notificationScheduleTimezone = shouldSkipNotificationSchedule
        ? null
        : (userTimezone ?? organizationTimezone);
    const activeBrowserNotificationsRef = useRef(
        new Map<string, Notification>(),
    );
    // Notification ids whose OS notification was already created this
    // session. The backend can redeliver the same notification event
    // repeatedly (a wedged agent run redelivered ~1/second for days), and
    // activeBrowserNotificationsRef drops an id when its notification
    // closes — so gating on that map alone re-creates the OS notification
    // after the user dismisses it. Each id pops at most once per session.
    const shownBrowserNotificationIdsRef = useRef(new Set<string>());
    const pendingBrowserNotificationEventsRef = useRef(
        new Map<string, RealtimeNotificationEvent>(),
    );
    const surfacedToastNotificationIdsRef = useRef(new Set<string>());
    const surfacedConversationToastIdsRef = useRef(
        new Map<string, string | null>(),
    );
    const conversationToastTimestampsMsRef = useRef<Array<number>>([]);
    // Last org_seq seen on this connection; null until the first stamped
    // event arrives (and after reconnects, where the reconnect refresh
    // already covers whatever the gap would have caught).
    const lastOrgSeqRef = useRef<number | null>(null);
    useEffect(() => {
        lastOrgSeqRef.current = null;
    }, [organizationId]);
    const recentConversationToastKeysRef = useRef(new Map<string, number>());
    // Approval alerts whose pending-approval gate is currently in flight.
    // A duplicate websocket delivery inside that window must not start a
    // second gate (and double-pop) — the first delivery decides.
    const inFlightApprovalGateIdsRef = useRef(new Set<string>());
    const notificationPreferencesRef = useRef(notificationPreferences);
    const modeRef = useRef(effectiveMode);
    const browserNotificationsEnabled =
        options?.browserNotificationsEnabled ?? false;
    const notificationChannelEnabled =
        options?.notificationChannelEnabled ?? true;
    const notificationClickBehavior =
        options?.notificationClickBehavior ?? 'navigate';
    const skipInAppNotificationPreferenceChecks =
        options?.skipInAppNotificationPreferenceChecks ?? false;
    const telemetrySurface =
        options?.telemetrySurface ??
        (notificationClickBehavior === 'new-tab' ? 'extension' : 'web_app');

    const captureNotificationEvent = useCallback(
        (
            eventName: string,
            eventData: RealtimeNotificationEvent,
            properties?: Record<string, unknown>,
        ) => {
            posthog.capture(eventName, {
                surface: telemetrySurface,
                click_behavior: notificationClickBehavior,
                notification_id: getNotificationId(eventData),
                notification_type: eventData.type,
                notification_status: getNotificationStatus(eventData),
                notification_mode: getNotificationMode(eventData),
                resource_type: eventData.resource.type,
                resource_id: eventData.resource.id,
                relationship_types: eventData.relationships.map(
                    (relationship) => relationship.type,
                ),
                ...properties,
            });
        },
        [notificationClickBehavior, posthog, telemetrySurface],
    );

    useEffect(() => {
        notificationPreferencesRef.current = notificationPreferences;
    }, [notificationPreferences]);

    useEffect(() => {
        modeRef.current = effectiveMode;
    }, [effectiveMode]);

    useEffect(() => {
        const recentConversationToastKeys =
            recentConversationToastKeysRef.current;
        pendingBrowserNotificationEventsRef.current.clear();
        surfacedToastNotificationIdsRef.current.clear();
        shownBrowserNotificationIdsRef.current.clear();
        inFlightApprovalGateIdsRef.current.clear();
        conversationToastTimestampsMsRef.current = [];
        recentConversationToastKeys.clear();

        const surfacedConversationToastIds =
            surfacedConversationToastIdsRef.current;
        return () => {
            for (const [
                toastId,
                clickableClassName,
            ] of surfacedConversationToastIds) {
                toast.dismiss(toastId);
                if (clickableClassName !== null) {
                    unregisterClickableToast(clickableClassName);
                }
            }
            surfacedConversationToastIds.clear();
            conversationToastTimestampsMsRef.current = [];
            recentConversationToastKeys.clear();
        };
    }, [organizationId, userId]);

    const showBrowserNotificationForEvent = useCallback(
        (eventData: RealtimeNotificationEvent) => {
            const notificationId = getNotificationId(eventData);
            // A redelivered event must not re-pop (or re-create) the OS
            // notification the user may have already dismissed.
            if (shownBrowserNotificationIdsRef.current.has(notificationId)) {
                return;
            }
            // Same viewing-suppression as the in-app toast: no OS-level
            // alert for the agent chat the user is currently looking at.
            const browserNavigationTarget =
                getNotificationNavigationTarget(eventData);
            if (
                isViewingAgentConversation(
                    browserNavigationTarget,
                    organizationId,
                )
            ) {
                return;
            }

            if (
                !browserNotificationsEnabled ||
                !shouldShowBrowserNotificationForEvent(eventData) ||
                typeof Notification === 'undefined' ||
                Notification.permission !== 'granted' ||
                !shouldShowNativeBrowserNotification()
            ) {
                pendingBrowserNotificationEventsRef.current.delete(
                    notificationId,
                );
                return;
            }

            const currentNotificationPreferences =
                notificationPreferencesRef.current;
            if (currentNotificationPreferences === undefined) {
                pendingBrowserNotificationEventsRef.current.set(
                    notificationId,
                    eventData,
                );
                return;
            }

            const notificationMode = getNotificationMode(eventData);
            if (
                notificationMode !== null &&
                notificationMode !== modeRef.current
            ) {
                pendingBrowserNotificationEventsRef.current.delete(
                    notificationId,
                );
                return;
            }

            const status = getNotificationStatus(eventData);
            // Native browser notifications only fire when the tab is hidden
            // or unfocused (see shouldShowNativeBrowserNotification above) —
            // they are a substitute delivery path for the same transient
            // pop-up alert as the sonner toast, not a persistent bell/store
            // record. Gate on the Web (in-app) channel so turning off Web
            // notifications for a type also stops this fallback.
            const webEnabledForStatus =
                skipInAppNotificationPreferenceChecks ||
                isNotificationChannelEnabled(
                    currentNotificationPreferences,
                    status,
                    'inapp',
                    {
                        skipSchedule: shouldSkipNotificationSchedule,
                        timezone: notificationScheduleTimezone,
                    },
                );
            // The pop-up master switch ("turn off pop-ups") silences the
            // transient sonner toast; a native browser notification is the
            // same class of transient pop-up, so the master switch must
            // silence it too. The sonner path enforces this via
            // isToastSuppressed; this fallback path has to enforce it itself.
            const popupMasterSwitchOff =
                currentNotificationPreferences?.toast_global_enabled === false;

            if (
                currentNotificationPreferences?.browser_enabled !== true ||
                !webEnabledForStatus ||
                popupMasterSwitchOff
            ) {
                pendingBrowserNotificationEventsRef.current.delete(
                    notificationId,
                );
                return;
            }

            const externalContent = getExternalNotificationContent(eventData);
            const notificationTitle = externalContent.title;
            const notificationMessage = externalContent.message;
            const navigationTarget = getNotificationNavigationTarget(eventData);
            const navigationKind = navigationTarget?.kind ?? null;
            const browserNotificationTitle = notificationTitle
                ? `Phoebe | ${notificationTitle}`
                : 'Phoebe';
            activeBrowserNotificationsRef.current.get(notificationId)?.close();

            const browserNotification = new Notification(
                browserNotificationTitle,
                {
                    body: notificationMessage ?? undefined,
                    tag: notificationId,
                },
            );
            shownBrowserNotificationIdsRef.current.add(notificationId);

            captureNotificationEvent(
                EVENTS.NOTIFICATION_BROWSER_SHOWN,
                eventData,
                {
                    channel: 'browser',
                    has_navigation_target: navigationTarget !== null,
                    navigation_kind: navigationKind,
                },
            );

            browserNotification.onclick = () => {
                captureNotificationEvent(
                    EVENTS.NOTIFICATION_CLICKED,
                    eventData,
                    {
                        channel: 'browser',
                        navigation_kind: navigationKind,
                    },
                );
                browserNotification.close();
                activeBrowserNotificationsRef.current.delete(notificationId);
                window.focus();

                if (!navigationTarget) {
                    return;
                }

                if (
                    navigationTarget.kind === 'agent_conversation' &&
                    notificationClickBehavior !== 'new-tab' &&
                    openAgentConversation({
                        organizationId,
                        agentConversationId:
                            navigationTarget.agentConversationId,
                        agentRunId: navigationTarget.agentRunId,
                    })
                ) {
                    return;
                }

                if (notificationClickBehavior === 'new-tab') {
                    if (navigationTarget.kind === 'agent_conversation') {
                        persistAgentChatOpenIntent({
                            ...navigationTarget,
                            organizationId,
                            userId,
                        });
                    }
                    window.open(
                        buildNotificationNavigationUrl(
                            navigationTarget,
                            organizationId,
                            notificationMode === 'live' ||
                                notificationMode === 'sandbox'
                                ? notificationMode
                                : null,
                        ),
                        '_blank',
                        'noopener,noreferrer',
                    );
                    return;
                }

                void navigate(
                    getNotificationNavigationRouteOptions(
                        navigationTarget,
                        organizationId,
                        notificationMode === 'live' ||
                            notificationMode === 'sandbox'
                            ? notificationMode
                            : null,
                    ),
                );
            };
            browserNotification.onclose = () => {
                activeBrowserNotificationsRef.current.delete(notificationId);
            };
            activeBrowserNotificationsRef.current.set(
                notificationId,
                browserNotification,
            );
            pendingBrowserNotificationEventsRef.current.delete(notificationId);
        },
        [
            browserNotificationsEnabled,
            captureNotificationEvent,
            navigate,
            notificationClickBehavior,
            notificationScheduleTimezone,
            organizationId,
            shouldSkipNotificationSchedule,
            skipInAppNotificationPreferenceChecks,
            userId,
        ],
    );

    useEffect(() => {
        if (!browserNotificationsEnabled) {
            pendingBrowserNotificationEventsRef.current.clear();
        } else if (notificationPreferences === undefined) {
            return;
        } else if (notificationPreferences.browser_enabled === true) {
            return;
        } else {
            pendingBrowserNotificationEventsRef.current.clear();
        }

        for (const notification of activeBrowserNotificationsRef.current.values()) {
            notification.close();
        }
        activeBrowserNotificationsRef.current.clear();
    }, [browserNotificationsEnabled, notificationPreferences]);

    useEffect(() => {
        if (
            notificationPreferences === undefined ||
            !browserNotificationsEnabled ||
            notificationPreferences.browser_enabled !== true ||
            pendingBrowserNotificationEventsRef.current.size === 0
        ) {
            return;
        }

        const pendingEvents = [
            ...pendingBrowserNotificationEventsRef.current.values(),
        ];
        pendingBrowserNotificationEventsRef.current.clear();
        for (const pendingEvent of pendingEvents) {
            // Queued approval alerts re-check the run before replaying: the
            // approval may have been decided while preferences loaded.
            const approvalAgentRunId =
                pendingEvent.resource.type === 'agent_run'
                    ? pendingEvent.resource.id
                    : null;
            if (approvalAgentRunId !== null && organizationId) {
                void agentRunStillAwaitsApproval(
                    organizationId,
                    approvalAgentRunId,
                    modeRef.current,
                ).then((stillAwaitsApproval) => {
                    if (stillAwaitsApproval) {
                        showBrowserNotificationForEvent(pendingEvent);
                    }
                });
            } else {
                showBrowserNotificationForEvent(pendingEvent);
            }
        }
    }, [
        browserNotificationsEnabled,
        notificationPreferences,
        organizationId,
        showBrowserNotificationForEvent,
    ]);

    useEffect(() => {
        const activeBrowserNotifications =
            activeBrowserNotificationsRef.current;
        const pendingBrowserNotificationEvents =
            pendingBrowserNotificationEventsRef.current;

        return () => {
            pendingBrowserNotificationEvents.clear();
            for (const notification of activeBrowserNotifications.values()) {
                notification.close();
            }
            activeBrowserNotifications.clear();
        };
    }, []);

    // Deliberately carries no credential. It is the effect's identity in
    // `useWebSocket`, so anything per-connection in here would re-run the
    // effect — resetting the backoff and the give-up budget — every time
    // credential changed. The credential is attached per attempt by
    // `resolveConnectUrl` below.
    const wsUrl =
        organizationId && userId
            ? `${getRuntimeConfig().apiUrl.replace(/^http(s?)/, 'ws$1')}/organizations/${organizationId}/events/user/${userId}/ws`
            : null;

    const resolveConnectUrl = useCallback(
        async (baseUrl: string): Promise<ConnectCredential> => {
            // Same-site tabs can carry the session cookie on the handshake.
            // Extension surfaces mint a ticket over HTTP instead, including
            // after an embedded sidepanel follows a deep link away from
            // `/extension`. The mint uses the bearer token when present or the
            // session cookie after the token is cleared.
            if (!organizationId) {
                return { kind: 'url', url: baseUrl };
            }
            const needsWebsocketTicket =
                getExtensionSessionToken() !== null ||
                isExtensionContext() ||
                isEmbeddedExtensionFrame();
            if (!needsWebsocketTicket) {
                return { kind: 'url', url: baseUrl };
            }
            try {
                const { ticket } =
                    await createOrganizationWebsocketTicket(organizationId);
                const url = new URL(baseUrl);
                url.searchParams.set('token', ticket);
                return { kind: 'url', url: url.toString() };
            } catch (error) {
                // A rejected session cannot be recovered by minting again, and
                // the give-up budget only guards sockets that never
                // connected —
                // so without this a socket that established once would re-mint
                // on every backoff forever once its session died.
                //
                // Read off the error rather than `instanceof`: the class comes
                // from another package, and an identity mismatch would fail
                // open into exactly that endless re-mint.
                const status = (error as { status?: unknown })?.status;
                if (status === 401 || status === 403) {
                    return { kind: 'terminal' };
                }
                // Anything else is transient; the hook backs off and retries.
                throw error;
            }
        },
        [organizationId],
    );

    const handleOrganizationEvent = useCallback(
        (eventData: OrganizationEvent) => {
            const isNotificationEvent = isRealtimeNotificationEvent(eventData);

            switch (eventData.type) {
                case 'incoming_call': {
                    if (eventData.data.organization_id !== organizationId) {
                        return;
                    }
                    // Phase 2 intentionally stops at a visible debug seam.
                    // Product ringing and softphone behavior belong to PR5+.
                    console.debug(
                        '[IncomingCallDebug] Validated incoming_call event',
                        eventData.data,
                    );
                    break;
                }
                case 'notification_info':
                case 'notification_success':
                case 'notification_error':
                case 'notification_warning': {
                    if (!notificationChannelEnabled) {
                        break;
                    }

                    if (
                        !isNotificationRecipient(
                            getNotificationPayloadData(eventData)?.[
                                'recipient_user_ids'
                            ],
                            userId,
                        )
                    ) {
                        return;
                    }

                    const status = getNotificationStatus(eventData);
                    const matchesMode = (() => {
                        const notificationMode = getNotificationMode(eventData);
                        return (
                            notificationMode === null ||
                            notificationMode === modeRef.current
                        );
                    })();
                    const webEnabledForStatus =
                        skipInAppNotificationPreferenceChecks
                            ? true
                            : isNotificationChannelEnabled(
                                  notificationPreferencesRef.current,
                                  status,
                                  'inapp',
                                  {
                                      skipSchedule:
                                          shouldSkipNotificationSchedule,
                                      timezone: notificationScheduleTimezone,
                                  },
                              );

                    if (!matchesMode || !webEnabledForStatus) {
                        break;
                    }

                    const notificationTitle = getInAppNotificationText(
                        eventData,
                        'in_app_title',
                        eventData.data.title,
                    );
                    const notificationMessage = getInAppNotificationText(
                        eventData,
                        'in_app_message',
                        eventData.data.message,
                    );
                    const notificationId = getNotificationId(eventData);
                    const stableNotificationId =
                        eventData.data.notification_id ?? null;
                    const navigationTarget =
                        getNotificationNavigationTarget(eventData);
                    const navigationKind = navigationTarget?.kind ?? null;
                    const couldSurfaceToast = !isViewingAgentConversation(
                        navigationTarget,
                        organizationId,
                    );
                    const notificationMode = getNotificationMode(eventData);
                    const normalizedNotificationMode: Mode | null =
                        notificationMode === 'live' ||
                        notificationMode === 'sandbox'
                            ? notificationMode
                            : null;
                    const clickableToastClassName =
                        couldSurfaceToast && navigationTarget !== null
                            ? createClickableToastClassName(notificationId)
                            : null;

                    captureNotificationEvent(
                        EVENTS.NOTIFICATION_RECEIVED,
                        eventData,
                        {
                            has_navigation_target: navigationTarget !== null,
                            navigation_kind: navigationKind,
                        },
                    );

                    const surfaceNotificationAlerts = ({
                        approvalChatOpened = false,
                    }: {
                        readonly approvalChatOpened?: boolean;
                    } = {}) => {
                        // Dedupe at surface time, not receive time: a pop the
                        // approval gate suppresses must not burn the id, or a
                        // redelivery of the same notification would stay
                        // silent even with an approval pending again.
                        let shouldSurfaceToast =
                            !approvalChatOpened &&
                            couldSurfaceToast &&
                            (stableNotificationId === null ||
                                !surfacedToastNotificationIdsRef.current.has(
                                    stableNotificationId,
                                ));
                        const shouldBoundToast = shouldBoundConversationToast(
                            eventData,
                            telemetrySurface,
                        );
                        let conversationToastSuppression: ConversationToastSuppression | null =
                            null;
                        if (
                            shouldSurfaceToast &&
                            shouldRateLimitToast(eventData)
                        ) {
                            // The rate limit bounds every info/success
                            // toast on every page, but the duplicate-key
                            // check only applies to conversation toasts —
                            // a deterministic dedupe key only exists for
                            // those. Error/warning toasts never reach
                            // this block, so they never spend the shared
                            // budget.
                            const nowMs = Date.now();
                            const cutoffMs = nowMs - TOAST_BURST_WINDOW_MS;
                            conversationToastTimestampsMsRef.current =
                                conversationToastTimestampsMsRef.current.filter(
                                    (timestampMs) => timestampMs > cutoffMs,
                                );

                            let toastKey: string | null = null;
                            let isDuplicateConversationToast = false;
                            if (shouldBoundToast) {
                                for (const [
                                    key,
                                    surfacedAtMs,
                                ] of recentConversationToastKeysRef.current) {
                                    if (surfacedAtMs <= cutoffMs) {
                                        recentConversationToastKeysRef.current.delete(
                                            key,
                                        );
                                    }
                                }

                                toastKey = buildConversationToastKey(
                                    eventData,
                                    navigationTarget,
                                );
                                isDuplicateConversationToast =
                                    recentConversationToastKeysRef.current.has(
                                        toastKey,
                                    );
                            }

                            if (isDuplicateConversationToast) {
                                conversationToastSuppression = 'duplicate';
                            } else if (
                                conversationToastTimestampsMsRef.current
                                    .length >= MAX_TOASTS_PER_BURST
                            ) {
                                conversationToastSuppression = 'throttled';
                            } else {
                                if (toastKey !== null) {
                                    recentConversationToastKeysRef.current.set(
                                        toastKey,
                                        nowMs,
                                    );
                                }
                                conversationToastTimestampsMsRef.current.push(
                                    nowMs,
                                );
                            }
                        }

                        if (conversationToastSuppression !== null) {
                            captureNotificationEvent(
                                conversationToastSuppression === 'duplicate'
                                    ? EVENTS.NOTIFICATION_TOAST_DEDUPED
                                    : EVENTS.NOTIFICATION_TOAST_THROTTLED,
                                eventData,
                                {
                                    channel: 'toast',
                                    reason: conversationToastSuppression,
                                },
                            );
                            shouldSurfaceToast = false;
                        }
                        if (
                            shouldSurfaceToast &&
                            stableNotificationId !== null
                        ) {
                            surfacedToastNotificationIdsRef.current.add(
                                stableNotificationId,
                            );
                        }
                        if (
                            shouldSurfaceToast &&
                            clickableToastClassName !== null &&
                            navigationTarget !== null
                        ) {
                            registerClickableToast(
                                clickableToastClassName,
                                () => {
                                    captureNotificationEvent(
                                        EVENTS.NOTIFICATION_CLICKED,
                                        eventData,
                                        {
                                            channel: 'toast',
                                            navigation_kind: navigationKind,
                                        },
                                    );
                                    // Acting on the toast counts as reading
                                    // the notification: retract the toast and
                                    // mark it read so the bell (this tab and
                                    // others, via the notification_state_updated
                                    // fan-out) stops showing it as unread.
                                    if (
                                        notificationId !== null &&
                                        organizationId !== null
                                    ) {
                                        void markNotificationsRead(
                                            organizationId,
                                            [notificationId],
                                            'toast-click-mark-read',
                                        ).catch(() => {
                                            // Best-effort: the bell still
                                            // converges on its next fetch.
                                        });
                                    }
                                    if (
                                        notificationClickBehavior === 'new-tab'
                                    ) {
                                        if (
                                            navigationTarget.kind ===
                                            'agent_conversation'
                                        ) {
                                            persistAgentChatOpenIntent({
                                                ...navigationTarget,
                                                organizationId,
                                                userId,
                                            });
                                        }
                                        window.open(
                                            buildNotificationNavigationUrl(
                                                navigationTarget,
                                                organizationId,
                                                normalizedNotificationMode,
                                            ),
                                            '_blank',
                                            'noopener,noreferrer',
                                        );
                                        return;
                                    }

                                    // Open the waiting agent chat in place;
                                    // fall back to route navigation if no
                                    // chat surface has registered an opener.
                                    if (
                                        navigationTarget.kind ===
                                            'agent_conversation' &&
                                        openAgentConversation({
                                            organizationId,
                                            agentConversationId:
                                                navigationTarget.agentConversationId,
                                            agentRunId:
                                                navigationTarget.agentRunId,
                                        })
                                    ) {
                                        return;
                                    }

                                    void navigate(
                                        getNotificationNavigationRouteOptions(
                                            navigationTarget,
                                            organizationId,
                                            normalizedNotificationMode,
                                        ),
                                    );
                                },
                            );
                        }

                        if (shouldSurfaceToast) {
                            // Deterministic per-notification toast id, so
                            // read/dismiss anywhere can retract the toast and
                            // redeliveries update in place instead of
                            // stacking.
                            const notificationToastId =
                                notificationId !== null &&
                                organizationId !== null
                                    ? buildNotificationToastId(
                                          organizationId,
                                          notificationId,
                                      )
                                    : null;
                            const conversationToastId = shouldBoundToast
                                ? notificationToastId
                                : null;
                            const cleanupToast = () => {
                                if (clickableToastClassName !== null) {
                                    unregisterClickableToast(
                                        clickableToastClassName,
                                    );
                                }
                                if (
                                    conversationToastId !== null &&
                                    surfacedConversationToastIdsRef.current.get(
                                        conversationToastId,
                                    ) === clickableToastClassName
                                ) {
                                    surfacedConversationToastIdsRef.current.delete(
                                        conversationToastId,
                                    );
                                }
                            };
                            if (conversationToastId !== null) {
                                surfacedConversationToastIdsRef.current.set(
                                    conversationToastId,
                                    clickableToastClassName,
                                );
                            }
                            const toastOptions = {
                                description: notificationMessage,
                                action: (
                                    <ToastControlsMenu
                                        organizationId={organizationId}
                                    />
                                ),
                                ...(notificationToastId !== null
                                    ? { id: notificationToastId }
                                    : {}),
                                ...(clickableToastClassName !== null
                                    ? {
                                          className: `cursor-pointer ${clickableToastClassName}`,
                                      }
                                    : {}),
                                ...(conversationToastId !== null ||
                                clickableToastClassName !== null
                                    ? {
                                          onAutoClose: cleanupToast,
                                          onDismiss: cleanupToast,
                                      }
                                    : {}),
                            };

                            if (eventData.type === 'notification_info') {
                                toast.info(notificationTitle ?? 'Info', {
                                    ...toastOptions,
                                    persistNotification: false,
                                });
                            } else if (
                                eventData.type === 'notification_success'
                            ) {
                                toast.success(notificationTitle ?? 'Success', {
                                    ...toastOptions,
                                    persistNotification: false,
                                });
                            } else if (
                                eventData.type === 'notification_error'
                            ) {
                                toast.error(notificationTitle ?? 'Error', {
                                    ...toastOptions,
                                    persistNotification: false,
                                });
                            } else {
                                toast.warning(notificationTitle ?? 'Warning', {
                                    ...toastOptions,
                                    persistNotification: false,
                                });
                            }

                            captureNotificationEvent(
                                EVENTS.NOTIFICATION_TOAST_SHOWN,
                                eventData,
                                {
                                    channel: 'toast',
                                    has_navigation_target:
                                        navigationTarget !== null,
                                    navigation_kind: navigationKind,
                                },
                            );
                        }

                        showBrowserNotificationForEvent(eventData);
                    };

                    // "Approval needed" alerts must mirror the chat surface:
                    // pop only while the run still has an approval card to
                    // act on. Keyed on the event's own resource (the run),
                    // not the derived navigation target, so a run-scoped
                    // alert without a conversation relationship is still
                    // gated. Approval alerts are the only notification kind
                    // with an agent_run resource, so every other kind
                    // surfaces exactly as before.
                    const approvalAgentRunId =
                        eventData.resource.type === 'agent_run'
                            ? eventData.resource.id
                            : null;
                    if (approvalAgentRunId !== null && organizationId) {
                        if (
                            stableNotificationId !== null &&
                            inFlightApprovalGateIdsRef.current.has(
                                stableNotificationId,
                            )
                        ) {
                            break;
                        }
                        if (stableNotificationId !== null) {
                            inFlightApprovalGateIdsRef.current.add(
                                stableNotificationId,
                            );
                        }
                        void agentRunStillAwaitsApproval(
                            organizationId,
                            approvalAgentRunId,
                            modeRef.current,
                        ).then((stillAwaitsApproval) => {
                            if (stableNotificationId !== null) {
                                inFlightApprovalGateIdsRef.current.delete(
                                    stableNotificationId,
                                );
                            }
                            if (stillAwaitsApproval) {
                                // On the web app, the approval card itself is
                                // the most useful pop-up: the coordinator can
                                // decide immediately instead of opening a
                                // passive toast first. Extension alerts still
                                // open a new tab only after an explicit click.
                                const approvalChatOpened =
                                    couldSurfaceToast &&
                                    notificationClickBehavior !== 'new-tab' &&
                                    notificationPreferencesRef.current
                                        ?.toast_global_enabled !== false &&
                                    navigationTarget?.kind ===
                                        'agent_conversation' &&
                                    openAgentConversation({
                                        organizationId,
                                        agentConversationId:
                                            navigationTarget.agentConversationId,
                                        agentRunId: navigationTarget.agentRunId,
                                    });
                                surfaceNotificationAlerts({
                                    approvalChatOpened,
                                });
                            }
                        });
                    } else {
                        surfaceNotificationAlerts();
                    }
                    break;
                }
                case 'notification_state_updated': {
                    for (const update of eventData.data.updates) {
                        if (!update.is_read && !update.is_dismissed) {
                            continue;
                        }

                        const activeNotification =
                            activeBrowserNotificationsRef.current.get(
                                update.notification_id,
                            );
                        if (!activeNotification) {
                            continue;
                        }

                        activeNotification.close();
                        activeBrowserNotificationsRef.current.delete(
                            update.notification_id,
                        );
                    }
                    break;
                }
                default:
                    break;
            }

            if (isNotificationEvent && !notificationChannelEnabled) {
                return;
            }
            organizationEventBus.dispatchEvent(
                new CustomEvent<OrganizationEventBusDetail<OrganizationEvent>>(
                    eventData.type,
                    {
                        detail: {
                            organizationId,
                            event: eventData,
                        },
                    },
                ),
            );
        },
        [
            captureNotificationEvent,
            navigate,
            notificationChannelEnabled,
            notificationClickBehavior,
            notificationScheduleTimezone,
            organizationId,
            shouldSkipNotificationSchedule,
            showBrowserNotificationForEvent,
            skipInAppNotificationPreferenceChecks,
            telemetrySurface,
            userId,
        ],
    );

    const trackOrganizationEventSequence = (orgSeq: number) => {
        const lastSeq = lastOrgSeqRef.current;
        if (lastSeq !== null && orgSeq <= lastSeq) {
            // A duplicate or lower sequence is a redelivery, never a gap.
            // Sequence 1 specifically means the server-side counter reset
            // (TTL expiry or flush) — adopt it as the new baseline; anything
            // else keeps the high-water mark so a reordered pair cannot
            // rewind the baseline and fake a gap on the next event.
            if (orgSeq === 1) {
                lastOrgSeqRef.current = 1;
            }
            return;
        }
        lastOrgSeqRef.current = orgSeq;
        if (lastSeq === null || !organizationId) {
            return;
        }
        // A jump of more than one means at least one published event never
        // reached this client.
        if (orgSeq > lastSeq + 1) {
            posthog.capture(EVENTS.WEBSOCKET_EVENT_SEQUENCE_GAP_DETECTED, {
                organization_id: organizationId,
                missed_events: orgSeq - lastSeq - 1,
            });
            organizationEventBus.dispatchEvent(
                new CustomEvent(ORGANIZATION_EVENTS_GAP_DETECTED_EVENT, {
                    detail: { organizationId },
                }),
            );
        }
    };

    const onMessage = (event: MessageEvent) => {
        let rawData: unknown;
        try {
            rawData = JSON.parse(event.data);
        } catch (error) {
            reportWebSocketEventParseError(error);
            return;
        }
        // The sequence is tracked from the raw frame, before and independent
        // of full event parsing: unknown forward-compat event types and the
        // server's sequence-only suppression stubs must advance the baseline
        // too, or a delivered-but-ignored frame would read as a false gap.
        const sequenceParse =
            organizationEventSequenceSchema.safeParse(rawData);
        if (sequenceParse.success) {
            trackOrganizationEventSequence(sequenceParse.data.org_seq);
        }
        const parsed = organizationEventSchema.safeParse(rawData);
        if (parsed.success) {
            // An organization shares one channel across modes. Events from
            // an older server carry no mode and must still be handled.
            if (
                typeof parsed.data.mode === 'string' &&
                parsed.data.mode !== modeRef.current
            ) {
                return;
            }
            handleOrganizationEvent(parsed.data);
            return;
        }
        // Forward compatibility: a newer server may emit event types this
        // (possibly older) client build does not know about yet. Ignore those
        // silently rather than reporting schema-drift noise; only flag a
        // genuinely malformed payload for a type we DO claim to support.
        if (isKnownOrganizationEventType(rawData)) {
            reportWebSocketEventParseError(parsed.error);
        }
    };

    useEffect(() => {
        const testHostname =
            typeof window === 'undefined' ? null : window.location.hostname;
        if (
            testHostname === null ||
            !isOrganizationEventTestEnvironment(
                testHostname,
                getRuntimeConfig().runEnvironment === 'development',
            )
        ) {
            return;
        }

        const testWindow = window as unknown as {
            __phoebeDispatchOrganizationEventForTests__?: (
                event: OrganizationEvent,
            ) => void;
        };
        const dispatchEventForTests = (event: OrganizationEvent) => {
            handleOrganizationEvent(organizationEventSchema.parse(event));
        };

        testWindow.__phoebeDispatchOrganizationEventForTests__ =
            dispatchEventForTests;

        return () => {
            if (
                testWindow.__phoebeDispatchOrganizationEventForTests__ ===
                dispatchEventForTests
            ) {
                delete testWindow.__phoebeDispatchOrganizationEventForTests__;
            }
        };
    }, [handleOrganizationEvent]);

    useWebSocket(wsUrl, onMessage, {
        // The org events endpoint echoes keepalives, so this socket can tell
        // a live connection from a silently-dead one. Conversations,
        // notifications and agent runs all ride on this one connection.
        heartbeat: true,
        resolveConnectUrl,
        // The socket cannot mint itself a new credential, but the session
        // provider can: it re-runs `whoami`, and in the sidepanel that retries
        // over the cookie once a rejected extension token has been cleared. A
        // confirmed session then re-arms this socket. Without it a tab that
        // stays visible and online — the sidepanel's whole lifetime — held a
        // dead feed until the user signed out and back in.
        onTerminalCredential: revalidateSession,
        onLivenessTimeout: () => {
            // How often a socket actually goes silent is the only evidence
            // that can tune the keepalive and liveness intervals.
            posthog.capture(EVENTS.WEBSOCKET_LIVENESS_TIMEOUT, {
                organization_id: organizationId,
            });
        },
        onClose: () => {
            if (!organizationId) {
                return;
            }

            organizationEventBus.dispatchEvent(
                new CustomEvent(ORGANIZATION_EVENTS_CONNECTION_CLOSED_EVENT, {
                    detail: { organizationId },
                }),
            );
        },
        onOpen: () => {
            if (!organizationId) {
                return;
            }

            organizationEventBus.dispatchEvent(
                new CustomEvent(ORGANIZATION_EVENTS_CONNECTION_OPENED_EVENT, {
                    detail: { organizationId },
                }),
            );
        },
        onReconnect: () => {
            // Events published while disconnected are gone; the reconnect
            // refresh below covers them, so restart gap tracking cleanly.
            lastOrgSeqRef.current = null;
            if (!organizationId) {
                return;
            }

            organizationEventBus.dispatchEvent(
                new CustomEvent(ORGANIZATION_EVENTS_RECONNECTED_EVENT, {
                    detail: { organizationId },
                }),
            );
        },
    });
}
